import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  ChatStateService,
  ChatMessage,
} from '../../../../services/chat-state.service';
import { MatTooltip } from '@angular/material/tooltip';
import { BotStatus } from '../../../../services/feature-services/chatbot.service';
import { UtilityService } from '../../../../services/utility.service';
import { SafeMarkdownPipe } from '../../../../pipes/safe-markdown.pipe';
import {
  clampDockWidth,
  defaultDockWidth,
  getSendIconSrc,
  isDesktopDock,
} from '../../../../utils/dock-resize.util';
import { TextareaInputComponent } from '../../form/textarea-input/textarea-input.component';

const FEEDBACK_COMMENT_MAX = 2000;

@Component({
  selector: 'app-chat-dock',
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    ReactiveFormsModule,
    SafeMarkdownPipe,
    TextareaInputComponent,MatTooltip
  ],
  templateUrl: './chat-dock.component.html',
  styleUrl: './chat-dock.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatDockComponent implements OnInit, OnDestroy {
  @ViewChild('messagesList') messagesListRef!: ElementRef<HTMLDivElement>;

  readonly MAX_INPUT_LENGTH = 1000;
  readonly FEEDBACK_COMMENT_MAX = FEEDBACK_COMMENT_MAX;

  replyControl = new FormControl('', [Validators.maxLength(1000)]);
  isLoading = false;
  userScrolledUp = false;
  botStatus: BotStatus | null = null;

  commentMsgId: string | null = null;
  commentControl = new FormControl('', [Validators.maxLength(FEEDBACK_COMMENT_MAX)]);

  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private draggableWidth = 0;

  private readonly destroy$ = new Subject<void>();

  private get viewportWidth(): number {
    return isPlatformBrowser(this.platformId) ? window.innerWidth : 0;
  }


  @HostBinding('style.width.px')
  get hostWidth(): number | null {
    return isDesktopDock(this.viewportWidth) && this.draggableWidth > 0
      ? this.draggableWidth
      : null;
  }

  /** Character count for the reply input — avoids null-coalescing noise in the template. */
  get inputCharCount(): number {
    return this.replyControl.value?.length ?? 0;
  }

  /** Character count for the thumbs-down comment input. */
  get commentCharCount(): number {
    return this.commentControl.value?.length ?? 0;
  }

  /**
   * Unified disabled state for the send / stop button.
   * True when there is nothing to send and the bot is not generating.
   */
  get isSendDisabled(): boolean {
    return (!this.replyControl.value?.trim() && !this.isLoading) || this.replyControl.invalid;
  }

  /** Resolves the send-button icon based on whether the input has content. */
  get sendIconSrc(): string {
    return getSendIconSrc(!!this.replyControl.value?.trim());
  }

  /** CSS modifier class for the status indicator dot. */
  get statusDotClass(): string {
    switch (this.botStatus?.status) {
      case 'online':      return 'chat-dock__status-dot--online';
      case 'degraded':    return 'chat-dock__status-dot--degraded';
      case 'unavailable': return 'chat-dock__status-dot--unavailable';
      default:            return 'chat-dock__status-dot--online';
    }
  }

  /** Label shown next to the status dot in the header. */
  get statusLabel(): string {
    return this.botStatus?.label ?? 'Online · answers from Aikosh only';
  }

  constructor(
    private el: ElementRef,
    public chatState: ChatStateService,
    public utilService: UtilityService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {
    this.chatState.isDockOpen$
      .pipe(takeUntil(this.destroy$), filter(Boolean))
      .subscribe(() => setTimeout(() => this.focusInput()));

    // Start status polling when dock opens, stop when it closes
    this.chatState.isDockOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        if (isOpen) {
          this.chatState.startStatusPolling();
        } else {
          this.chatState.stopStatusPolling();
        }
      });

    // Reflect live bot status in the header
    this.chatState.botStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.botStatus = status;
        this.cdr.markForCheck();
      });

    // Drive smart auto-scroll; rendering handled by async pipe via @let
    this.chatState.messages$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.userScrolledUp) {
        setTimeout(() => this.scrollToBottom());
      }
    });

    this.replyControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && value.length > 1000) {
          this.replyControl.setValue(value.slice(0, 1000), { emitEvent: false });
        }
        this.cdr.markForCheck();
      });

    this.chatState.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.chatState.stopStatusPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!this.isResizing) return;
    const delta = this.resizeStartX - e.clientX; // drag left = wider
    this.draggableWidth = clampDockWidth(
      this.viewportWidth,
      this.resizeStartWidth + delta,
    );
    this.chatState.setDockWidth(this.draggableWidth);
    this.cdr.markForCheck(); // OnPush: notify of HostBinding change
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isResizing = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const viewportWidth = this.viewportWidth;

    if (!isDesktopDock(viewportWidth)) {
      this.draggableWidth = 0;
      this.chatState.setDockWidth(0);
      this.cdr.markForCheck();
      return;
    }

    if (this.draggableWidth > 0) {
      const clamped = clampDockWidth(viewportWidth, this.draggableWidth);
      if (clamped !== this.draggableWidth) {
        this.draggableWidth = clamped;
        this.chatState.setDockWidth(clamped);
        this.cdr.markForCheck();
      }
    } else {
      this.chatState.setDockWidth(defaultDockWidth(viewportWidth));
    }
  }

  onMessagesScroll(el: HTMLElement): void {
    const BOTTOM_THRESHOLD = 80; // px — within this distance = "at bottom"
    this.userScrolledUp =
      el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_THRESHOLD;
  }

  scrollToBottom(): void {
    const el = this.messagesListRef?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    this.userScrolledUp = false;
  }

  private focusInput(): void {
    this.el.nativeElement.querySelector('textarea')?.focus();
  }

  onResizerMouseDown(e: MouseEvent): void {
    if (!isDesktopDock(this.viewportWidth)) return;
    this.isResizing = true;
    this.resizeStartX = e.clientX;
    this.resizeStartWidth = this.el.nativeElement.offsetWidth;
    e.preventDefault();
  }

  onEnterKey(e: KeyboardEvent): void {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    this.sendReply();
  }

  onSuffixAction(): void {
    if (this.isLoading) {
      this.chatState.stopGenerating();
    } else {
      this.sendReply();
    }
  }

  sendReply(): void {
    const text = this.replyControl.value?.trim();
    if (!text || this.chatState.isLoading || this.replyControl.invalid) return;
    this.userScrolledUp = false;
    this.chatState.sendMessage(text);
    this.replyControl.reset('');
    setTimeout(() => this.scrollToBottom());
  }

  retryFrom(msg: ChatMessage): void {
    this.userScrolledUp = false;
    this.chatState.retryFrom(msg.id);
  }

 
  onFeedbackClick(msg: ChatMessage, rating: 'up' | 'down'): void {
    if (msg.feedbackPending) return;
    if (rating === 'down' && msg.turn_id) {
      if (this.commentMsgId === msg.id) {
        // Second click on 👎 while form is already open — just close form, no API call
        this.dismissComment();
      } else {
        this.commentMsgId = msg.id;
        this.commentControl.reset('');
        this.cdr.markForCheck();
      }
    } else {
      // 👍 — submit immediately and close any open comment form
      this.dismissComment();
      this.chatState.submitFeedback(msg.id, rating, null);
    }
  }

  
  submitComment(msg: ChatMessage): void {
    if (this.commentControl.invalid || msg.feedbackPending) return;
    const comment = this.commentControl.value?.trim() || null;
    this.chatState.submitFeedback(msg.id, 'down', comment);
    this.commentMsgId = null;
    this.commentControl.reset('');
    this.cdr.markForCheck();
  }

  skipComment(msg: ChatMessage): void {
    if (msg.feedbackPending) return;
    this.chatState.submitFeedback(msg.id, 'down', null);
    this.commentMsgId = null;
    this.commentControl.reset('');
    this.cdr.markForCheck();
  }

  dismissComment(): void {
    this.commentMsgId = null;
    this.commentControl.reset('');
    this.cdr.markForCheck();
  }

  clearChat(): void {
    this.chatState.clearMessages();
    this.chatState.closeDock();

  }

  close(): void {
    this.chatState.closeDock();
  }
  onChatLinkClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const target = event.target as HTMLElement;

    const copyBtn = target.closest('.chat-link-copy-btn') as HTMLElement | null;
    if (copyBtn) {
      event.preventDefault();
      event.stopPropagation();
      const url = copyBtn.getAttribute('data-copy-url') ?? '';
      if (url) this.utilService.copyText('', url);
      return;
    }

    const anchor = target.closest('a') as HTMLAnchorElement | null;
    if (!anchor?.href) return;

    try {
      const url = new URL(anchor.href);
      if (url.origin === this.document.location.origin) {
        event.preventDefault();
        this.router.navigateByUrl(url.pathname + url.search + url.hash);
      }
    } catch {
    }
  }

  // ── Template helper methods ──────────────────────────────────────────────────

  /** @for track function — returns stable message id. */
  trackById(_: number, msg: ChatMessage): string {
    return msg.id;
  }

  /**
   * Returns the content render state for a bot message bubble.
   * Used with @switch in the template to avoid multiple overlapping @if blocks.
   *
   *  'loading'   → streaming started but no text yet (animated dots)
   *  'streaming' → streaming in progress with text available (text + cursor)
   *  'done'      → streaming finished (final text only)
   */
  getMsgContentState(msg: ChatMessage): 'loading' | 'streaming' | 'done' {
    switch (true) {
      case msg.isStreaming && !msg.text: return 'loading';
      case !!msg.isStreaming:            return 'streaming';
      default:                           return 'done';
    }
  }

  getFeedbackState(msg: ChatMessage): 'thanks' | 'buttons' | 'none' {
    switch (true) {
      case !msg.turn_id:            return 'none';
      case !!msg.feedbackSubmitted: return 'thanks';
      default:                      return 'buttons';
    }
  }

  showActionRow(msg: ChatMessage): boolean {
    return !msg.isStreaming && !!msg.text && msg.turn_id !== undefined && !msg.isSystemMessage;
  }

  /** Whether the thumbs-down comment form should be visible for a given message. */
  showCommentForm(msg: ChatMessage): boolean {
    return this.commentMsgId === msg.id && !msg.feedbackSubmitted;
  }

  getThumbSrc(msg: ChatMessage, direction: 'up' | 'down'): string {
    switch (msg.feedback) {
      case 'up':   return direction === 'up'   ? 'images/black-thumbs.png' : 'images/thumbs.svg';
      case 'down': return direction === 'down' ? 'images/black-thumbs.png' : 'images/thumbs.svg';
      default:     return 'images/thumbs.svg';
    }
  }

  // ── Date / time helpers ──────────────────────────────────────────────────────

  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const messages = this.chatState.messages;
    const prev = messages[index - 1];
    const curr = messages[index];
    if (!prev || !curr) return false;
    return !this.utilService.isSameDay(new Date(prev.timestamp), new Date(curr.timestamp));
  }
}
